import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Box,
  Typography,
  Chip,
  CircularProgress,
  Paper,
} from "@mui/material";
import { useState, useEffect } from "react";
import dayjs from "dayjs";
import httpclient from "../../../Utils";
import { useApiErrorHandler } from "../../../Hooks/useApiErrorHandler";

const PreviewScheduleDialog = ({
  open,
  handleClose,
  scheduleId,
}) => {
  const [scheduleData, setScheduleData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleApiError = useApiErrorHandler();

  useEffect(() => {
    if (open && scheduleId) {
      fetchScheduleDetails();
    }
  }, [open, scheduleId]);

  useEffect(() => {
    if (!open) {
      setScheduleData(null);
      setError("");
    }
  }, [open]);

  const fetchScheduleDetails = async () => {
    setLoading(true);
    try {
      const response = await httpclient.get("/delivery-truck-schedule");
      
      if (response.data.status === 200) {
        const schedule = response.data.data.find(item => item.id === scheduleId);
        if (schedule) {
          setScheduleData(schedule);
        } else {
          setError("Schedule not found");
        }
      }
    } catch (error) {
      handleApiError(error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return dateString ? dayjs(dateString).format("DD-MM-YYYY hh:mm A") : "-";
  };

  // Info card component for consistent styling
  const InfoCard = ({ title, value }) => (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        height: "100%",
        backgroundColor: "#F8F9FA",
        borderRadius: "8px",
        border: "1px solid #E0E0E0",
      }}
    >
      <Typography
        variant="subtitle2"
        color="textSecondary"
        sx={{
          mb: 1,
          fontSize: "12px",
          fontWeight: 600,
          textTransform: "uppercase",
          letterSpacing: "0.5px",
        }}
      >
        {title}
      </Typography>
      <Typography
        variant="body1"
        sx={{
          fontSize: "16px",
          fontWeight: 500,
          color: "#1A1C1E",
          wordBreak: "break-word",
        }}
      >
        {value || "-"}
      </Typography>
    </Paper>
  );

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby="preview-dialog-title"
      maxWidth="md"
      fullWidth
    >
      <DialogTitle 
        id="preview-dialog-title" 
        sx={{ 
          pb: 2,
          borderBottom: "1px solid #E0E0E0",
          mb: 2,
        }}
      >
        <Typography variant="h6" fontWeight={600}>
          Schedule Details
        </Typography>
        {/* <Typography variant="body2" color="textSecondary">
          ID: {scheduleData?.id || "-"}
        </Typography> */}
      </DialogTitle>

      <DialogContent>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <Typography color="error">{error}</Typography>
          </Box>
        ) : scheduleData ? (
          <Grid container spacing={2}>
            {/* Row 1: Group Name and Time */}
            <Grid item xs={12} md={6}>
              <InfoCard title="Group Name" value={scheduleData.group_name} />
            </Grid>
            <Grid item xs={12} md={6}>
              <InfoCard title="Time" value={scheduleData.time} />
            </Grid>

            {/* Row 2: Created At and Created By */}
            <Grid item xs={12} md={6}>
              <InfoCard 
                title="Created At" 
                value={formatDate(scheduleData.created_at)} 
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <InfoCard title="Created By" value={scheduleData.created_by} />
            </Grid>

            {/* Row 3: Updated At and Updated By - Always shown with "-" if not available */}
            <Grid item xs={12} md={6}>
              <InfoCard 
                title="Updated At" 
                value={formatDate(scheduleData.updated_at)} 
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <InfoCard 
                title="Updated By" 
                value={scheduleData.updated_by} 
              />
            </Grid>

            {/* Pulling Bays - Full width at the bottom */}
            <Grid item xs={12}>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  backgroundColor: "#F8F9FA",
                  borderRadius: "8px",
                  border: "1px solid #E0E0E0",
                  mt: 2,
                }}
              >
                <Typography
                  variant="subtitle2"
                  color="textSecondary"
                  sx={{
                    mb: 2,
                    fontSize: "12px",
                    fontWeight: 600,
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                  }}
                >
                  Pulling Bays
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                  {scheduleData.selected_bays && scheduleData.selected_bays.length > 0 ? (
                    scheduleData.selected_bays.map((bay) => (
                      <Chip
                        key={bay.id}
                        label={bay.bay}
                        sx={{
                          backgroundColor: "#7152F3",
                          color: "white",
                          fontWeight: 500,
                          "&:hover": {
                            backgroundColor: "#5c34fa",
                          },
                        }}
                      />
                    ))
                  ) : (
                    <Typography variant="body2" color="textSecondary">
                      No bays selected
                    </Typography>
                  )}
                </Box>
              </Paper>
            </Grid>
          </Grid>
        ) : (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <Typography color="textSecondary">No data available</Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 2 }}>
        <Button
          variant="contained"
          onClick={handleClose}
          sx={{
            backgroundColor: "#7152F3",
            "&:hover": { backgroundColor: "#5c34fa" },
            px: 4,
            py: 1,
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PreviewScheduleDialog;